import { motion } from "framer-motion";
import { Coffee, Wine, Beer, CupSoda, GlassWater, Droplets, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { BEVERAGES } from "@/lib/mbNutrition";
import { addMeal, setWater, WATER_TARGET, recordWaterGoal } from "@/lib/mbStorage";

const ICONS = {
  coffee: Coffee,
  wine: Wine,
  beer: Beer,
  soda: CupSoda,
  cup: GlassWater,
  water: Droplets,
};

export const Beverages = ({ water, onWaterChange, onDrinkAdded }) => {
  const changeWater = (delta) => {
    const next = setWater(water + delta);
    if (next >= WATER_TARGET) recordWaterGoal();
    onWaterChange(next);
    if (delta > 0 && next === WATER_TARGET) {
      toast.success("Obiettivo idratazione raggiunto! 💧", {
        description: `${WATER_TARGET} bicchieri d'acqua oggi. Ottimo lavoro!`,
      });
    }
  };

  const addBeverage = (bev) => {
    addMeal({
      dish_name: bev.name,
      calories: bev.kcal,
      protein: 0,
      carbs: 0,
      fat: 0,
      ingredients: [],
      meal_type: "Bevanda",
      tip: bev.awareness,
      kind: "drink",
    });
    if (bev.id === "acqua") {
      changeWater(1);
    }
    onDrinkAdded?.();
    toast.success(`${bev.name} aggiunta`, {
      description: bev.awareness,
      duration: 5000,
    });
  };

  const pct = Math.min(100, Math.round((water / WATER_TARGET) * 100));

  return (
    <div
      className="bg-white rounded-2xl p-4 mb-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)] border border-slate-200"
      data-testid="beverages-section"
    >
      <h3 className="font-display text-sm font-bold text-slate-800 flex items-center gap-1.5 mb-3">
        <Droplets size={15} className="text-[#2cd4bf]" /> Bevande & Idratazione
      </h3>

      {/* Water tracker */}
      <div className="bg-[#f0fdfa] border border-teal-100 rounded-xl p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-semibold text-slate-600 flex items-center gap-1">
            <Droplets size={13} className="text-[#2cd4bf]" /> Acqua oggi
          </span>
          <span className="text-[13px] font-bold text-slate-800 font-display" data-testid="water-count">
            {water}<span className="text-[11px] font-medium text-slate-400"> / {WATER_TARGET} bicchieri</span>
          </span>
        </div>
        <div className="w-full h-1.5 bg-teal-100 rounded-full overflow-hidden mb-2.5">
          <motion.div
            className="h-full bg-[#2cd4bf] rounded-full"
            initial={false}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => changeWater(-1)}
            data-testid="water-minus"
            className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-500 text-xs font-semibold active:scale-95 transition-transform"
          >
            <Minus size={13} /> Rimuovi
          </button>
          <button
            onClick={() => changeWater(1)}
            data-testid="water-plus"
            className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-[#2cd4bf] text-white text-xs font-semibold active:scale-95 transition-transform"
          >
            <Plus size={13} /> Bicchiere
          </button>
        </div>
      </div>

      {/* Quick add beverages */}
      <span className="text-[10px] uppercase tracking-wide text-slate-400 font-bold block mb-2">
        Aggiungi rapida
      </span>
      <div className="grid grid-cols-3 gap-2" data-testid="beverage-grid">
        {BEVERAGES.map((bev) => {
          const Icon = ICONS[bev.icon] || CupSoda;
          return (
            <button
              key={bev.id}
              onClick={() => addBeverage(bev)}
              data-testid={`add-drink-${bev.id}`}
              className="flex flex-col items-center gap-1 py-2.5 px-1 rounded-xl bg-slate-50 border border-slate-100 hover:border-[#2b90ef] hover:bg-[#f0f7ff] transition-colors active:scale-95"
            >
              <Icon size={18} className="text-[#2b90ef]" />
              <span className="text-[10px] font-semibold text-slate-700 leading-tight text-center">{bev.name}</span>
              <span className="text-[9px] text-slate-400">{bev.kcal} kcal</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
